import { z } from 'zod'

import { DataSource, EXT_MAP } from './shared'

/**
 * The kinds of data a user brings into Panorama from their own files. Each one
 * has its own screen, its own formats, and its own rules about which files have
 * to arrive together.
 */
export enum ImportSurface {
  Prescriptions = 'prescriptions',
  Boundaries = 'boundaries',
  FieldList = 'field-list',
  AsAppliedData = 'as-applied-data'
}

export const importSurfaceSchema = z.enum(ImportSurface)

/**
 * A file input's `accept` silently matches nothing when an extension arrives
 * without its leading dot, so the type requires it.
 */
export type ImportFileExtension = `.${string}`

/**
 * The filename's extension in the catalog's spelling — dotted, lowercased — or
 * undefined when it has none.
 */
export const fileExtension = (filename: string): ImportFileExtension | undefined => {
  const dot = filename.lastIndexOf('.')
  return dot === -1 ? undefined : (filename.slice(dot).toLowerCase() as ImportFileExtension)
}

type ImportFormat = {
  /** What a grower calls the format. */
  name: string
  /** File extensions, spelled as they appear on disk. */
  extensions: readonly [ImportFileExtension, ...ImportFileExtension[]]
  /**
   * Extensions an exporter writes beside this format that Panorama reads
   * nothing from. These are dropped by the client; we allow users to pick them
   * in their file-selector because they believe they are not able to upload all
   * their data otherwise. Explicitly described as ignored to avoid tricking Aurora.
   */
  ignoredExtensions?: readonly ImportFileExtension[]
  /** What the user has to get right for a file of this format to be accepted. */
  requirement?: string
}

/**
 * Describes the user's data and what Panorama does with it — never the app's
 * chrome. Where a screen sits, what it is titled, and who may open it all
 * change without anything here failing, so those are answered by performing
 * the navigation (OpenImportScreen and its refusal reasons), not by prose.
 */
type ImportSurfaceGuide = {
  /** What the import takes in, as a noun phrase, for picking between surfaces. */
  subject: string
  /** What the import brings into Panorama, and what the user gets out of it. */
  imports: string
  formats: readonly [ImportFormat, ...ImportFormat[]]
}

const SHAPEFILE_PARTS = ['.shp', '.shx', '.dbf'] as const

/**
 * The sidecars a GIS platform may write beside a shapefile. Panorama reads
 * coordinates as lat/lon and attributes straight from the .dbf, so none of
 * these are consulted — but a grower exporting from QGIS or Ops Center gets
 * them whether they want them or not, and refusing the selection reads as
 * though their export is wrong.
 */
const SHAPEFILE_IGNORED = [
  '.prj',
  '.cpg',
  '.sbn',
  '.sbx',
  '.qix',
  '.qmd',
  '.atx',
  '.ain',
  '.aih',
  '.fbn',
  '.fbx',
  '.ixs',
  '.mxs'
] as const

/**
 * What Panorama accepts from a file, per import surface, in the terms a user
 * would recognize. The clients build their file pickers from `extensions`, and
 * Aurora answers "what will the import take?" from the whole entry, so a format
 * Panorama gains is one edit here rather than one per surface that has to
 * describe it.
 */
export const IMPORT_SURFACES: Readonly<Record<ImportSurface, ImportSurfaceGuide>> = {
  [ImportSurface.Prescriptions]: {
    subject: 'variable-rate prescriptions',
    imports:
      'Variable-rate prescriptions, uploaded in bulk and then assigned to the fields they belong to.',
    formats: [
      {
        name: 'Shapefile',
        extensions: SHAPEFILE_PARTS,
        ignoredExtensions: SHAPEFILE_IGNORED,
        requirement:
          'All three files, sharing one base name, selected together. A prescription needs the .dbf — that is where its rates live.'
      },
      { name: 'Zipped shapefile', extensions: ['.zip'] },
      { name: '20|20 file', extensions: ['.2020'] }
    ]
  },
  [ImportSurface.Boundaries]: {
    subject: 'field boundaries',
    imports:
      'Field boundaries, uploaded in bulk and then assigned to the fields they belong to. One boundary per field.',
    formats: [
      {
        name: 'Shapefile',
        extensions: SHAPEFILE_PARTS,
        ignoredExtensions: SHAPEFILE_IGNORED,
        requirement:
          'The .shp and .shx, sharing one base name, selected together. The .dbf is optional for a boundary.'
      },
      { name: 'Zipped shapefile', extensions: ['.zip'] },
      { name: '20|20 file', extensions: ['.2020'] }
    ]
  },
  [ImportSurface.FieldList]: {
    subject: 'the client, farm, and field list (a CFF)',
    imports:
      'The client, farm, and field list (a CFF) the rest of the operation is organized by. The same import can instead copy field names out of data already uploaded to the operation, with no file at all.',
    formats: [
      {
        name: 'Spreadsheet',
        extensions: ['.xlsx', '.csv'],
        requirement: 'Columns named Client, Farm, and Field.'
      },
      { name: '20|20 file', extensions: ['.2020'] }
    ]
  },
  [ImportSurface.AsAppliedData]: {
    subject: 'as-applied data from passes already run',
    imports:
      'As-applied data off a display or another brand’s platform — the passes the map, reports, and summaries are built from.',
    formats: [
      { name: 'Gen3 20|20', extensions: [EXT_MAP[DataSource.Gen3]] },
      {
        name: 'Precision Planting SeedSense (Gen2)',
        extensions: [EXT_MAP[DataSource.Gen2]],
        requirement: 'Chosen as a whole folder rather than as individual files.'
      },
      {
        name: 'John Deere Operations Center export',
        extensions: [EXT_MAP[DataSource.JDShapefile]]
      },
      { name: 'ISOXML', extensions: [EXT_MAP[DataSource.ISOXML]] }
    ]
  }
}

/**
 * The `accept` value for a file input. Offers ignored extensions so a grower
 * can select a whole export and let the client drop the files it does not read.
 */
export const importFileAccept = (...surfaces: ImportSurface[]): string =>
  [
    ...new Set(
      surfaces.flatMap((surface) =>
        IMPORT_SURFACES[surface].formats.flatMap((format) => [
          ...format.extensions,
          ...(format.ignoredExtensions ?? [])
        ])
      )
    )
  ].join(',')
